import React, { useState } from "react";
import axios from "axios";
import styles from "./CadProPage.module.scss";

const S3_URL =
  "https://ddukeong-blob.s3.ap-northeast-2.amazonaws.com/installer/CadPro_Bundle_Setup_1.0.7+(1).zip";
const FILE_NAME = "CadPro_Bundle_Setup_1.0.7+(1).zip";

const FEATURES = [
  {
    category: "공통",
    icon: "settings",
    color: "#3b82f6",
    items: [
      "도면 정리 자동화",
      "레이어 일괄 변환",
      "블록 일괄 교체",
      "도면 비교",
      "자동 번호 매기기",
      "테이블 자동 생성",
      "면적/길이 자동 산출",
      "좌표 추출",
      "일괄 출력(배치 플롯)",
      "축척 자동 설정",
      "도면 정보 추출",
      "DWG 일괄 변환",
    ],
  },
  {
    category: "건축",
    icon: "apartment",
    color: "#8b5cf6",
    items: [
      "실 면적 자동 산출",
      "마감재 물량 산출",
      "창호 일람표 생성",
      "구조 부재 단면 표시",
      "실명 자동 입력",
      "층별 면적표 생성",
    ],
  },
  {
    category: "토목",
    icon: "terrain",
    color: "#059669",
    items: [
      "횡단면도 자동 작성",
      "토공 물량 산출",
      "종단면도 생성",
      "노선 좌표 산출",
      "측점별 토적 계산",
    ],
  },
  {
    category: "조경",
    icon: "park",
    color: "#d97706",
    items: [
      "수목 범례표 자동 생성",
      "식재 수량 산출",
      "조경 면적 산출",
    ],
  },
  {
    category: "전기",
    icon: "bolt",
    color: "#ef4444",
    items: [
      "배선 길이 자동 산출",
      "전등/콘센트 수량 산출",
      "간선 사이즈 자동 표기",
    ],
  },
];

const CadProPage: React.FC = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await axios.get(S3_URL, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", FILE_NAME);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <span className={styles.badge}>AutoCAD Plugin</span>
          <h1 className={styles.heroTitle}>CadPro</h1>
          <p className={styles.heroSubtitle}>
            AutoCAD 도면 작업을 자동화하는 플러그인
          </p>
          <div className={styles.heroActions}>
            <button
              className={styles.heroDownloadBtn}
              onClick={handleDownload}
              disabled={downloading}
            >
              <span className="material-symbols-outlined">download</span>
              {downloading ? "다운로드 중..." : "무료 다운로드"}
            </button>
          </div>
          <p className={styles.heroRequirements}>
            AutoCAD 2015~2025 &middot; Windows 10/11 &middot; .NET 4.7.2+
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.features}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>주요 기능</h2>
          <p className={styles.sectionDesc}>
            5개 분야, 총 29개 자동화 기능을 제공합니다
          </p>
          <div className={styles.featureGrid}>
            {FEATURES.map((cat) => (
              <div key={cat.category} className={styles.featureCard}>
                <div
                  className={styles.featureIcon}
                  style={{ background: cat.color }}
                >
                  <span className="material-symbols-outlined">{cat.icon}</span>
                </div>
                <h3 className={styles.featureCategoryName}>
                  {cat.category}
                  <span className={styles.featureCount}>
                    {cat.items.length}개
                  </span>
                </h3>
                <ul className={styles.featureList}>
                  {cat.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Guide Section */}
      <section className={styles.install}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>설치 방법</h2>
          <div className={styles.installStepsRow}>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>1</div>
              <p>다운로드한 ZIP 파일 압축 해제</p>
            </div>
            <div className={styles.stepArrow}>
              <span className="material-symbols-outlined">arrow_forward</span>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>2</div>
              <p><code>CadPro_Bundle_Setup.exe</code> 실행</p>
            </div>
            <div className={styles.stepArrow}>
              <span className="material-symbols-outlined">arrow_forward</span>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>3</div>
              <p>설치 완료 후 AutoCAD 재시작</p>
            </div>
            <div className={styles.stepArrow}>
              <span className="material-symbols-outlined">arrow_forward</span>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber}>4</div>
              <p><code>CADPRO_PALETTE</code> 명령 입력</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download + Contact Section */}
      <section className={styles.download}>
        <div className={styles.container}>
          <h2 className={styles.downloadTitle}>지금 바로 시작하세요</h2>
          <p className={styles.downloadDesc}>
            CadPro로 도면 작업 시간을 절약하세요
          </p>
          <button
            className={styles.downloadBtn}
            onClick={handleDownload}
            disabled={downloading}
          >
            <span className="material-symbols-outlined">download</span>
            {downloading ? "다운로드 중..." : "CadPro 다운로드"}
          </button>
          <div className={styles.downloadMeta}>
            <span>AutoCAD 2015~2025 호환</span>
          </div>
          <div className={styles.contactInfo}>
            <span className="material-symbols-outlined">mail</span>
            <a href="mailto:ddk@ddukeong.com">ddk@ddukeong.com</a>
            <span className={styles.divider}>|</span>
            <span className="material-symbols-outlined">call</span>
            <a href="tel:070-4799-2557">070-4799-2557</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CadProPage;
